import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMultiWeekProgram } from "../context/MultiWeekProgramContext";
import {
  BuilderShell,
  EditModeToolbar,
  MuscleDistributionPanel,
  PublishBar,
  PublishBarButton,
  SaveStatusPill,
  WeekdayScheduleGrid,
  WeeklyVolumeChart,
} from "../features/builders";
import { GearIcon, MetricTile, WeekRail } from "../components/product";
import { Button, Dialog, EmptyState, Sheet, Skeleton } from "../design-v2";
import {
  addDays,
  getDateKeyInTimeZone,
  resolveOccurrenceTemporalState,
} from "../features/multiWeek/occurrence";
import {
  getCycleDetailsPath,
  getCyclesLibraryPath,
  getCycleWorkoutEditorPath,
} from "../features/multiWeek/routes";
import {
  deleteCycle,
  openOrCreateCycleEditDraft,
  publishCycleDraft,
  updateUpcomingDraftTimeline,
} from "../services/api";
import {
  aggregateWorkoutMetrics,
  computeWorkoutMetrics,
} from "../utils/workoutMetrics";
import { usePlannedMuscleVolume } from "../features/muscleVolume/usePlannedMuscleVolume";

const WEEKDAY_ROWS = [
  { day: "MONDAY", label: "Monday", shortLabel: "M" },
  { day: "TUESDAY", label: "Tuesday", shortLabel: "T" },
  { day: "WEDNESDAY", label: "Wednesday", shortLabel: "W" },
  { day: "THURSDAY", label: "Thursday", shortLabel: "T" },
  { day: "FRIDAY", label: "Friday", shortLabel: "F" },
  { day: "SATURDAY", label: "Saturday", shortLabel: "S" },
  { day: "SUNDAY", label: "Sunday", shortLabel: "S" },
];
const WEEKDAY_INDEX = new Map(
  WEEKDAY_ROWS.map((entry, index) => [entry.day, index])
);
const MAX_CYCLE_DURATION_WEEKS = 8;

function formatDate(value) {
  if (!value) {
    return "";
  }

  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getTodayDateInput() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateInput(value) {
  return new Date(`${value}T00:00:00`);
}

function getNextMondayDateValue(dateValue = getTodayDateInput()) {
  const date = new Date(`${dateValue}T00:00:00`);
  const day = date.getDay();
  const daysUntilMonday = day === 1 ? 0 : day === 0 ? 1 : 8 - day;
  date.setDate(date.getDate() + daysUntilMonday);
  return formatDateInput(date);
}

function getStartOfMonthDate(dateValue) {
  const date = dateValue instanceof Date ? new Date(dateValue) : parseDateInput(dateValue);
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(dateValue, months) {
  const date = dateValue instanceof Date ? new Date(dateValue) : parseDateInput(dateValue);
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function formatMonthHeading(dateValue) {
  return dateValue.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function formatWeekRange(startDateValue) {
  const startDate = parseDateInput(startDateValue);
  const endDate = parseDateInput(addDays(startDateValue, 6));
  const sameMonth = startDate.getMonth() === endDate.getMonth();
  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  const startLabel = startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endLabel = endDate.toLocaleDateString("en-US", {
    month: sameMonth ? undefined : "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });

  return `${startLabel} to ${endLabel}`;
}

function buildWeekOptionsForMonth(monthDate, minStartDateValue, selectedStartDateValue = null) {
  const monthStart = getStartOfMonthDate(monthDate);
  const monthEnd = addDays(formatDateInput(addMonths(monthStart, 1)), -1);
  const firstWeekStart = parseDateInput(
    getNextMondayDateValue(addDays(formatDateInput(monthStart), -1))
  );
  const monthIndex = monthStart.getMonth();
  const year = monthStart.getFullYear();
  const normalizedSelectedValue =
    selectedStartDateValue && isMondayDateInput(selectedStartDateValue)
      ? selectedStartDateValue
      : null;
  const options = [];

  for (let cursor = firstWeekStart; formatDateInput(cursor) <= monthEnd;) {
    const optionValue = formatDateInput(cursor);
    const mondayBelongsToDisplayedMonth =
      cursor.getMonth() === monthIndex && cursor.getFullYear() === year;
    const isSelected = optionValue === normalizedSelectedValue;

    if (optionValue >= minStartDateValue && (mondayBelongsToDisplayedMonth || isSelected)) {
      options.push({
        value: optionValue,
        label: formatWeekRange(optionValue),
        isCurrentWeek: optionValue === minStartDateValue,
        isSelectedOutsideMonth: isSelected && !mondayBelongsToDisplayedMonth,
      });
    }

    cursor = parseDateInput(addDays(optionValue, 7));
  }

  if (
    normalizedSelectedValue &&
    !options.some((option) => option.value === normalizedSelectedValue) &&
    normalizedSelectedValue >= minStartDateValue
  ) {
    options.unshift({
      value: normalizedSelectedValue,
      label: formatWeekRange(normalizedSelectedValue),
      isCurrentWeek: normalizedSelectedValue === minStartDateValue,
      isSelectedOutsideMonth: true,
    });
  }

  return options;
}

function hasAllowedWeeksInMonth(monthDate, minStartDateValue) {
  return buildWeekOptionsForMonth(monthDate, minStartDateValue).length > 0;
}

function isMondayDateInput(value) {
  if (!value) {
    return false;
  }

  return new Date(`${value}T00:00:00`).getDay() === 1;
}

function formatMinutes(value) {
  return `${value}m`;
}

function hasCardioBlock(workout) {
  return (workout?.blocks || []).some((block) => block.type === "cardio");
}

function getMetricBarWidth(value, maxValue) {
  if (value <= 0 || maxValue <= 0) {
    return "0%";
  }

  return `${Math.max(8, Math.min(100, (value / maxValue) * 100))}%`;
}

function formatShortDateRange(startDateValue, endDateValue) {
  if (!startDateValue || !endDateValue) {
    return "";
  }

  const startDate = new Date(`${startDateValue}T00:00:00`);
  const endDate = new Date(`${endDateValue}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return "";
  }

  const startLabel = startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endLabel = endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  return `${startLabel} to ${endLabel}`;
}

function SettingsChevron({ direction = "down" }) {
  return (
    <svg
      className={`lz-builder-settings__chevron lz-builder-settings__chevron--${direction}`}
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <path d="M3.5 5.75 8 10.25l4.5-4.5" />
    </svg>
  );
}

function buildUiError(error, fallbackMessage) {
  return {
    message: error?.message || fallbackMessage,
    code: error?.code || null,
  };
}

function buildWorkoutAutosaveBlockedError(actionLabel, workoutIds, blockedReason = null) {
  const error = new Error(
    `Resolve workout autosave conflicts or errors before ${actionLabel}.`
  );
  error.code = "WORKOUT_AUTOSAVE_BLOCKED";
  error.workoutIds = workoutIds;
  error.blockedReason = blockedReason;
  return error;
}

// Publish conflicts that mean another tab or request changed this cycle:
// the draft must be reloaded from the latest version before publishing.
const PUBLISH_RELOAD_CODES = new Set([
  "NO_DRAFT_TO_PUBLISH",
  "CYCLE_PUBLISH_CONFLICT",
  "DRAFT_REVISION_CONFLICT",
  "PUBLISHED_PLAN_NOT_CURRENT",
]);

function formatRemovedOccurrence(occurrence) {
  const name = occurrence?.workoutName || "Workout";
  return occurrence?.plannedDate ? `${occurrence.plannedDate} - ${name}` : name;
}

function buildDraftAutosaveBlockedError(actionLabel) {
  const error = new Error(
    `Resolve draft autosave conflicts or errors before ${actionLabel}.`
  );
  error.code = "DRAFT_AUTOSAVE_BLOCKED";
  return error;
}

export default function ManualBuilderMulti() {
  const navigate = useNavigate();
  const { cycleId } = useParams();
  const {
    programDraft,
    draftMetadata,
    hydrateProgramDraft,
    beginHydrationTarget,
    handleDraftExpired,
    prepareCycleDraftForPublish,
    flushAllWorkouts,
    setSelectedWeek,
    updateDraftMetadata,
    updateProgramMeta,
    moveSelectedWeekWorkoutToScheduledDay,
    duplicateSelectedWeekWorkout,
    deleteSelectedWeekWorkout,
    reloadLatestAfterConflict,
  } = useMultiWeekProgram();
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isReloadingAfterConflict, setIsReloadingAfterConflict] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isDeletingCycle, setIsDeletingCycle] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [pendingScheduleSyncPublishedPlanId, setPendingScheduleSyncPublishedPlanId] =
    useState(null);
  const [removedRescheduledOccurrences, setRemovedRescheduledOccurrences] =
    useState(null);
  // A schedule-sync retry must carry the cancellation the user confirmed.
  const confirmedCancelRemovedRescheduledRef = useRef(false);
  const [isMuscleDistributionExpanded, setIsMuscleDistributionExpanded] =
    useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsProgramName, setSettingsProgramName] = useState("");
  const [settingsNameError, setSettingsNameError] = useState("");
  const [settingsStartDate, setSettingsStartDate] = useState("");
  const [settingsDurationWeeks, setSettingsDurationWeeks] = useState(1);
  const [settingsSourceWeekNumber, setSettingsSourceWeekNumber] = useState(null);
  const [settingsError, setSettingsError] = useState("");
  const [isSettingsWeekPickerOpen, setIsSettingsWeekPickerOpen] = useState(false);
  const [settingsWeekPickerMonth, setSettingsWeekPickerMonth] = useState(() =>
    getStartOfMonthDate(getTodayDateInput())
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteWorkoutConfirm, setShowDeleteWorkoutConfirm] = useState(false);
  const [showGraceWindowConfirm, setShowGraceWindowConfirm] = useState(false);
  const [showConflictReloadConfirm, setShowConflictReloadConfirm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedWorkoutOrderIndex, setSelectedWorkoutOrderIndex] = useState(null);
  const [planId, setPlanId] = useState(null);

  const todayDate = getTodayDateInput();
  const minSettingsStartDate = useMemo(() => getNextMondayDateValue(todayDate), [todayDate]);
  const currentDraftWeekCount = Math.max(
    1,
    programDraft.weeks.length || programDraft.programLength || 1
  );
  const isUpcomingCycle = draftMetadata.temporalStatus === "upcoming";
  const isActiveCycle = draftMetadata.temporalStatus === "active";
  const canEditTimelineSettings = isUpcomingCycle || isActiveCycle;
  const isStartDateEditable = isUpcomingCycle;
  const isDurationEditable = isUpcomingCycle || isActiveCycle;
  const isRecoveringDraft = draftMetadata.isRecoveringDraft;
  const activePlanId = draftMetadata.cyclePlanId || planId || null;
  const hasPublishTarget = Boolean(
    pendingScheduleSyncPublishedPlanId || activePlanId
  );
  const editTodayDateKey =
    draftMetadata.draftState?.localDate ||
    getDateKeyInTimeZone(
      draftMetadata.timezone || programDraft.timezone || "America/Toronto"
    );
  const isActiveEditMode = isEditMode && isActiveCycle;
  const currentActiveWeekNumber = useMemo(() => {
    if (!isActiveCycle || !programDraft.startDate || !editTodayDateKey) {
      return 1;
    }

    const startDate = parseDateInput(programDraft.startDate);
    const today = parseDateInput(editTodayDateKey);
    const diffMs = today.getTime() - startDate.getTime();
    const diffDays = Number.isFinite(diffMs) ? Math.floor(diffMs / 86400000) : 0;

    return Math.min(
      currentDraftWeekCount,
      Math.max(1, Math.floor(diffDays / 7) + 1)
    );
  }, [currentDraftWeekCount, editTodayDateKey, isActiveCycle, programDraft.startDate]);
  const settingsMinDurationWeeks = isActiveCycle ? currentActiveWeekNumber : 1;
  const settingsMaxDurationWeeks = MAX_CYCLE_DURATION_WEEKS;

  useEffect(() => {
    setPlanId(draftMetadata.cyclePlanId || null);
  }, [draftMetadata.cyclePlanId]);

  useEffect(() => {
    if (!cycleId) {
      setIsLoading(false);
      return undefined;
    }

    if (
      draftMetadata.loadedFromBackend &&
      draftMetadata.cycleId === cycleId &&
      draftMetadata.status === "draft"
    ) {
      // Already loaded for this cycle -- avoid an unconditional re-fetch on
      // every navigation back into the builder, which used to force-hydrate
      // a possibly-stale snapshot over in-flight/dirty local edits (the
      // reproduced navigation-revert bug). No spinner either, since nothing
      // needs to load.
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadDraft() {
      setIsLoading(true);
      setLoadError(null);

      try {
        // A genuine document switch reaches the terminal workout flush
        // barrier before this target is declared and fetched.
        await beginHydrationTarget({ cycleId, planId: null });
        const response = await openOrCreateCycleEditDraft(cycleId);
        const activePlanId = response?.planId || null;
        if (cancelled) {
          return;
        }

        setPlanId(activePlanId);
        hydrateProgramDraft(response);
        updateDraftMetadata({ cyclePlanId: activePlanId });
        setLoadError(null);
      } catch (loadError) {
        if (!cancelled) {
          setLoadError(buildUiError(loadError, "Unable to load cycle draft."));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadDraft();

    return () => {
      cancelled = true;
    };
  }, [
    cycleId,
    draftMetadata.cycleId,
    draftMetadata.loadedFromBackend,
    draftMetadata.status,
    beginHydrationTarget,
    hydrateProgramDraft,
    updateDraftMetadata,
  ]);

  useEffect(() => {
    if (
      draftMetadata.temporalStatus !== "active" ||
      !draftMetadata.draftState?.isGraceWindow ||
      !draftMetadata.draftState?.canExtendDraft ||
      draftMetadata.allowCrossDayDraft
    ) {
      return;
    }

    setShowGraceWindowConfirm(true);
  }, [draftMetadata, updateDraftMetadata]);

  const selectedWeek = useMemo(
    () =>
      programDraft.weeks.find((week) => week.weekNumber === programDraft.selectedWeek) ||
      programDraft.weeks[0] ||
      null,
    [programDraft.selectedWeek, programDraft.weeks]
  );

  const selectedWeekMetrics = useMemo(
    () => aggregateWorkoutMetrics(selectedWeek?.workouts || []),
    [selectedWeek]
  );
  const selectedWeekWorkouts = useMemo(
    () => selectedWeek?.workouts || [],
    [selectedWeek]
  );
  const selectedWeekMuscleVolume = usePlannedMuscleVolume(selectedWeekWorkouts);

  const weeklyVolumeData = useMemo(() => {
    const weeks = programDraft.weeks || [];
    const rows = weeks.map((week) => {
      const metrics = aggregateWorkoutMetrics(week.workouts || []);

      return {
        weekNumber: week.weekNumber,
        totalSetCount: metrics.totalSetCount,
        totalExerciseCount: metrics.totalExerciseCount,
        isSelected: programDraft.selectedWeek === week.weekNumber,
      };
    });

    const maxSets = rows.reduce((maxValue, week) => Math.max(maxValue, week.totalSetCount), 0);

    return rows.map((week) => ({
      ...week,
      width: getMetricBarWidth(week.totalSetCount, Math.max(1, maxSets)),
    }));
  }, [programDraft.selectedWeek, programDraft.weeks]);

  const topStats = useMemo(
    () => [
      {
        label: "Exercises",
        value: String(selectedWeekMetrics.totalExerciseCount),
        width: getMetricBarWidth(
          selectedWeekMetrics.totalExerciseCount,
          Math.max(1, (programDraft.sessionsPerWeek || 1) * 8)
        ),
      },
      {
        label: "Sets",
        value: String(selectedWeekMetrics.totalSetCount),
        width: getMetricBarWidth(
          selectedWeekMetrics.totalSetCount,
          Math.max(1, (programDraft.sessionsPerWeek || 1) * 20)
        ),
      },
      {
        label: "AVG. Time",
        value: formatMinutes(selectedWeekMetrics.averageDurationMinutes),
        width: getMetricBarWidth(selectedWeekMetrics.averageDurationMinutes, 120),
      },
      {
        label: "AVG. TUT",
        value: formatMinutes(selectedWeekMetrics.averageTUTMinutes),
        width: getMetricBarWidth(selectedWeekMetrics.averageTUTMinutes, 60),
      },
    ],
    [programDraft.sessionsPerWeek, selectedWeekMetrics]
  );

  const weekdayRows = useMemo(() => {
    const workouts = [...(selectedWeek?.workouts || [])].sort(
      (left, right) => (left.orderIndex || 0) - (right.orderIndex || 0)
    );
    const remainingWorkouts = [...workouts];
    const rows = WEEKDAY_ROWS.map((entry) => ({
      ...entry,
      workout: null,
    }));

    rows.forEach((row) => {
      const explicitIndex = remainingWorkouts.findIndex(
        (workout) => workout.scheduledDay === row.day
      );
      if (explicitIndex >= 0) {
        const [workout] = remainingWorkouts.splice(explicitIndex, 1);
        rows[WEEKDAY_ROWS.findIndex((entry) => entry.day === row.day)] = {
          ...row,
          workout,
        };
      }
    });

    remainingWorkouts.forEach((workout) => {
      const firstEmptyRow = rows.find((row) => !row.workout);
      if (firstEmptyRow) {
        firstEmptyRow.workout = workout;
      }
    });

    return rows.map((row, index) => {
      const occurrenceState = resolveOccurrenceTemporalState({
        cycleStartDate: programDraft.startDate,
        weekNumber: selectedWeek?.weekNumber || programDraft.selectedWeek || 1,
        scheduledDay: row.day,
        weekdayIndex: WEEKDAY_INDEX.get(row.day) || 0,
        todayDateKey: editTodayDateKey,
      });
      const {
        calendarDate,
        dateState,
        isPastOccurrence,
        isTodayOccurrence,
        isFutureOccurrence,
      } = occurrenceState;
      const workout = row.workout;
      if (!workout) {
        return {
          ...row,
          calendarDate,
          dateState,
          isPastOccurrence,
          isTodayOccurrence,
          isFutureOccurrence,
          meta: null,
          canMoveUp: false,
          canMoveDown: false,
          targetDayUp: null,
          targetDayDown: null,
        };
      }

      const metrics = computeWorkoutMetrics(workout);

      return {
        ...row,
        calendarDate,
        dateState,
        isPastOccurrence,
        isTodayOccurrence,
        isFutureOccurrence,
        workout: {
          ...workout,
          meta: `${metrics.exerciseCount} exercises • ${metrics.setCount} ${
            hasCardioBlock(workout) ? "strength sets" : "sets"
          } • ~${metrics.estimatedDurationMinutes} min • ${metrics.totalTUTMinutes}m TUT`,
        },
        targetDayUp: index > 0 ? WEEKDAY_ROWS[index - 1].day : null,
        targetDayDown: index < WEEKDAY_ROWS.length - 1 ? WEEKDAY_ROWS[index + 1].day : null,
      };
    });
  }, [editTodayDateKey, programDraft.selectedWeek, programDraft.startDate, selectedWeek]);

  const selectedWeekRangeLabel = useMemo(() => {
    if (!programDraft.startDate) {
      return "";
    }

    const currentWeekNumber = selectedWeek?.weekNumber || programDraft.selectedWeek || 1;
    const weekStart = addDays(programDraft.startDate, Math.max(0, (currentWeekNumber - 1) * 7));
    const weekEnd = addDays(weekStart, 6);
    return formatShortDateRange(weekStart, weekEnd);
  }, [programDraft.selectedWeek, programDraft.startDate, selectedWeek]);

  const selectedWorkoutRow = useMemo(
    () =>
      weekdayRows.find(
        (row) =>
          row.workout &&
          Number(row.workout.orderIndex) === Number(selectedWorkoutOrderIndex)
      ) || null,
    [selectedWorkoutOrderIndex, weekdayRows]
  );
  const selectedWorkoutRowIndex = useMemo(
    () =>
      weekdayRows.findIndex(
        (row) =>
          row.workout &&
          Number(row.workout.orderIndex) === Number(selectedWorkoutOrderIndex)
      ),
    [selectedWorkoutOrderIndex, weekdayRows]
  );
  const previousSelectedRow = useMemo(
    () =>
      selectedWorkoutRowIndex > 0 ? weekdayRows[selectedWorkoutRowIndex - 1] || null : null,
    [selectedWorkoutRowIndex, weekdayRows]
  );
  const nextSelectedRow = useMemo(
    () =>
      selectedWorkoutRowIndex >= 0 &&
        selectedWorkoutRowIndex < weekdayRows.length - 1
        ? weekdayRows[selectedWorkoutRowIndex + 1] || null
        : null,
    [selectedWorkoutRowIndex, weekdayRows]
  );

  const hasEmptyWeekdaySlot = useMemo(
    () => weekdayRows.some((row) => !row.workout),
    [weekdayRows]
  );
  const selectedWeekIsFullyPast = useMemo(
    () => Boolean(weekdayRows.length) && weekdayRows.every((row) => row.isPastOccurrence),
    [weekdayRows]
  );
  const firstAvailableDuplicateRow = useMemo(() => {
    if (!isActiveEditMode) {
      return null;
    }

    return weekdayRows.find((row) => !row.workout && !row.isPastOccurrence) || null;
  }, [isActiveEditMode, weekdayRows]);
  const canMoveSelectedPrevious = useMemo(() => {
    if (!selectedWorkoutRow?.workout) {
      return false;
    }

    if (!isActiveEditMode) {
      return Boolean(selectedWorkoutRow.targetDayUp);
    }

    if (selectedWeekIsFullyPast || selectedWorkoutRow.isPastOccurrence) {
      return false;
    }

    return Boolean(previousSelectedRow && !previousSelectedRow.isPastOccurrence);
  }, [isActiveEditMode, previousSelectedRow, selectedWeekIsFullyPast, selectedWorkoutRow]);
  const canMoveSelectedNext = useMemo(() => {
    if (!selectedWorkoutRow?.workout) {
      return false;
    }

    if (!isActiveEditMode) {
      return Boolean(selectedWorkoutRow.targetDayDown);
    }

    if (selectedWeekIsFullyPast || selectedWorkoutRow.isPastOccurrence) {
      return false;
    }

    return Boolean(nextSelectedRow && !nextSelectedRow.isPastOccurrence);
  }, [isActiveEditMode, nextSelectedRow, selectedWeekIsFullyPast, selectedWorkoutRow]);
  const canDeleteSelectedWorkout = useMemo(() => {
    if (!selectedWorkoutRow?.workout) {
      return false;
    }

    if (!isActiveEditMode) {
      return true;
    }

    if (selectedWeekIsFullyPast) {
      return false;
    }

    return !selectedWorkoutRow.isPastOccurrence;
  }, [isActiveEditMode, selectedWeekIsFullyPast, selectedWorkoutRow]);
  const canDuplicateSelectedWorkout = useMemo(() => {
    if (!selectedWorkoutRow?.workout) {
      return false;
    }

    if (!isActiveEditMode) {
      return hasEmptyWeekdaySlot;
    }

    if (selectedWeekIsFullyPast) {
      return false;
    }

    return Boolean(firstAvailableDuplicateRow);
  }, [
    firstAvailableDuplicateRow,
    hasEmptyWeekdaySlot,
    isActiveEditMode,
    selectedWeekIsFullyPast,
    selectedWorkoutRow,
  ]);

  useEffect(() => {
    setSelectedWorkoutOrderIndex(null);
  }, [programDraft.selectedWeek]);

  useEffect(() => {
    if (
      selectedWorkoutOrderIndex == null ||
      weekdayRows.some(
        (row) =>
          row.workout &&
          Number(row.workout.orderIndex) === Number(selectedWorkoutOrderIndex)
      )
    ) {
      return;
    }

    setSelectedWorkoutOrderIndex(null);
  }, [selectedWorkoutOrderIndex, weekdayRows]);

  const saveStatusLabel = useMemo(() => {
    if (draftMetadata.isRecoveringDraft) {
      return "Reloading...";
    }

    if (draftMetadata.saveState === "conflict") {
      return "Update available";
    }

    if (draftMetadata.saveState === "saving") {
      return "Saving...";
    }

    if (draftMetadata.saveState === "error") {
      return "Save failed";
    }

    if (draftMetadata.lastSavedAt) {
      return "Saved";
    }

    return "Draft";
  }, [draftMetadata.isRecoveringDraft, draftMetadata.lastSavedAt, draftMetadata.saveState]);

  const handleReloadAfterConflict = async () => {
    if (isReloadingAfterConflict) {
      return;
    }

    setShowConflictReloadConfirm(true);
  };

  const handleConfirmReloadAfterConflict = async () => {
    if (isReloadingAfterConflict) {
      return;
    }

    setShowConflictReloadConfirm(false);
    setIsReloadingAfterConflict(true);
    try {
      await reloadLatestAfterConflict();
    } finally {
      setIsReloadingAfterConflict(false);
    }
  };

  const handleAcceptGraceWindow = () => {
    setShowGraceWindowConfirm(false);
    updateDraftMetadata({ allowCrossDayDraft: true });
  };
  const autosaveError = useMemo(() => {
    if (draftMetadata.recoveryMessage || draftMetadata.saveState !== "error") {
      return null;
    }

    return {
      message: draftMetadata.lastSaveErrorMessage || "Unable to autosave this draft.",
      code: draftMetadata.lastSaveErrorCode || null,
    };
  }, [
    draftMetadata.lastSaveErrorCode,
    draftMetadata.lastSaveErrorMessage,
    draftMetadata.recoveryMessage,
    draftMetadata.saveState,
  ]);

  const settingsFinalWeekEndDate = useMemo(
    () =>
      addDays(
        settingsStartDate || todayDate,
        Math.max(0, settingsDurationWeeks * 7 - 1)
      ),
    [settingsDurationWeeks, settingsStartDate, todayDate]
  );
  const settingsEndDate = settingsFinalWeekEndDate;
  const selectedSettingsWeekRangeLabel = useMemo(
    () => (settingsStartDate ? formatWeekRange(settingsStartDate) : "Select a week"),
    [settingsStartDate]
  );
  const settingsVisibleWeekOptions = useMemo(
    () =>
      buildWeekOptionsForMonth(
        settingsWeekPickerMonth,
        minSettingsStartDate,
        settingsStartDate
      ),
    [minSettingsStartDate, settingsStartDate, settingsWeekPickerMonth]
  );
  const canGoToPreviousSettingsMonth = useMemo(
    () =>
      hasAllowedWeeksInMonth(addMonths(settingsWeekPickerMonth, -1), minSettingsStartDate),
    [minSettingsStartDate, settingsWeekPickerMonth]
  );
  const settingsWeekPickerMonthLabel = useMemo(
    () => formatMonthHeading(settingsWeekPickerMonth),
    [settingsWeekPickerMonth]
  );
  const isSettingsExtension = settingsDurationWeeks > currentDraftWeekCount;
  const settingsSourceWeekOptions = useMemo(
    () =>
      (programDraft.weeks || []).map((week) => {
        const weekStartDate = programDraft.startDate
          ? addDays(programDraft.startDate, Math.max(0, (week.weekNumber - 1) * 7))
          : null;

        return {
          value: week.weekNumber,
          label: `W${week.weekNumber}`,
          detail: weekStartDate ? formatWeekRange(weekStartDate) : week.label || `Week ${week.weekNumber}`,
        };
      }),
    [programDraft.startDate, programDraft.weeks]
  );
  const hasValidSettingsSourceWeek = useMemo(
    () =>
      settingsSourceWeekOptions.some(
        (option) => Number(option.value) === Number(settingsSourceWeekNumber)
      ),
    [settingsSourceWeekNumber, settingsSourceWeekOptions]
  );

  const openSettingsPanel = () => {
    setSettingsProgramName(programDraft.programName || "");
    setSettingsNameError("");
    setSettingsStartDate(programDraft.startDate || todayDate);
    setSettingsDurationWeeks(programDraft.programLength || currentDraftWeekCount);
    setSettingsSourceWeekNumber(
      programDraft.selectedWeek || programDraft.weeks[0]?.weekNumber || 1
    );
    setSettingsError("");
    setIsSettingsWeekPickerOpen(false);
    setShowDeleteConfirm(false);
    setIsSettingsOpen(true);
  };

  const closeSettingsPanel = () => {
    // Cancel discards the local field only; programDraft.programName is never
    // touched until Save.
    setSettingsNameError("");
    setSettingsError("");
    setIsSettingsWeekPickerOpen(false);
    setShowDeleteConfirm(false);
    setIsSettingsOpen(false);
  };

  const handleSettingsProgramNameChange = (value) => {
    setSettingsProgramName(value);
    setSettingsNameError("");
    setSettingsError("");
  };

  const handleSettingsStartDateChange = (value) => {
    setSettingsStartDate(value);
    setSettingsError("");
  };

  const handleSettingsDurationChange = (nextValue) => {
    const parsed = Number(nextValue);
    if (!Number.isFinite(parsed)) {
      setSettingsDurationWeeks(settingsMinDurationWeeks);
      return;
    }

    const safeDuration = Math.max(
      settingsMinDurationWeeks,
      Math.min(settingsMaxDurationWeeks, Math.trunc(parsed))
    );
    setSettingsDurationWeeks(safeDuration);
    if (
      safeDuration > currentDraftWeekCount &&
      !hasValidSettingsSourceWeek
    ) {
      setSettingsSourceWeekNumber(programDraft.selectedWeek || programDraft.weeks[0]?.weekNumber || 1);
    }
    setSettingsError("");
  };

  const decrementSettingsDuration = () => {
    handleSettingsDurationChange(
      Math.max(settingsMinDurationWeeks, settingsDurationWeeks - 1)
    );
  };

  const incrementSettingsDuration = () => {
    handleSettingsDurationChange(
      Math.min(settingsMaxDurationWeeks, settingsDurationWeeks + 1)
    );
  };

  const handleSaveSettings = async () => {
    if (!cycleId || !activePlanId || isRecoveringDraft) {
      return;
    }

    setSettingsError("");
    setSettingsNameError("");

    if (!canEditTimelineSettings) {
      setSettingsError("Timeline settings can only be edited for upcoming or active cycles.");
      return;
    }

    const nextProgramName = settingsProgramName.trim();

    if (!nextProgramName) {
      setSettingsNameError("Cycle name is required.");
      return;
    }

    if (!settingsStartDate) {
      setSettingsError("Choose a valid future start date.");
      return;
    }

    if (!isMondayDateInput(settingsStartDate)) {
      setSettingsError("Start date must be a Monday.");
      return;
    }

    if (settingsDurationWeeks < settingsMinDurationWeeks || settingsDurationWeeks > settingsMaxDurationWeeks) {
      setSettingsError(
        `Duration must stay between ${settingsMinDurationWeeks} and ${settingsMaxDurationWeeks} weeks.`
      );
      return;
    }

    if (isUpcomingCycle && (settingsStartDate < todayDate || settingsEndDate < todayDate)) {
      setSettingsError("Past dates are not allowed.");
      return;
    }

    if (isActiveCycle) {
      if (settingsStartDate !== programDraft.startDate) {
        setSettingsError("Start date cannot be changed on an active cycle.");
        return;
      }

      if (settingsDurationWeeks < currentActiveWeekNumber) {
        setSettingsError(`Duration cannot be shorter than week ${currentActiveWeekNumber}.`);
        return;
      }
    }

    if (isSettingsExtension && !hasValidSettingsSourceWeek) {
      setSettingsError("Choose one existing source week for the added weeks.");
      return;
    }

    setIsSavingSettings(true);
    setPublishError(null);

    try {
      const {
        blockedWorkoutIds = [],
        blockedReason = null,
      } = await flushAllWorkouts();
      if (blockedReason || blockedWorkoutIds.length > 0) {
        throw buildWorkoutAutosaveBlockedError(
          "saving cycle settings",
          blockedWorkoutIds,
          blockedReason
        );
      }

      if (draftMetadata.saveState === "error") {
        throw buildDraftAutosaveBlockedError("saving cycle settings");
      }

      const response = await updateUpcomingDraftTimeline(cycleId, activePlanId, {
        newStartDate: settingsStartDate,
        durationWeeks: settingsDurationWeeks,
        allowCrossDayDraft: draftMetadata.allowCrossDayDraft,
        ...(isSettingsExtension
          ? { sourceWeekNumber: Number(settingsSourceWeekNumber) }
          : {}),
      });
      setPlanId(response?.planId || activePlanId);
      hydrateProgramDraft(response);

      // The rename is applied AFTER hydration on purpose: the timeline response
      // carries the pre-rename document, so renaming first would be silently
      // reverted here. updateProgramMeta marks a structural mutation, which is
      // the same canonical whole-document draft save (700 ms debounce,
      // contentRevision/CAS, conflict handling) every other structural cycle
      // edit already uses -- no second persistence path, no new endpoint, and
      // the timeline request itself is left byte-identical.
      if (nextProgramName !== (response?.builderPayload?.programName ?? programDraft.programName)) {
        updateProgramMeta({ programName: nextProgramName });
      }

      closeSettingsPanel();
    } catch (saveError) {
      setSettingsError(saveError.message || "Unable to update timeline settings.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleDeleteCycle = async () => {
    if (isRecoveringDraft) {
      return;
    }

    if (!cycleId || !showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setIsDeletingCycle(true);
    setPublishError(null);

    try {
      await deleteCycle(cycleId);
      navigate(getCyclesLibraryPath(), { replace: true });
    } catch (deleteError) {
      setSettingsError(deleteError.message || "Unable to delete this cycle.");
      setIsDeletingCycle(false);
    }
  };

  const publishCycle = async ({ cancelRemovedRescheduled = false } = {}) => {
    if (
      !cycleId ||
      isRecoveringDraft ||
      (!pendingScheduleSyncPublishedPlanId &&
        (!activePlanId || draftMetadata.saveState === "conflict"))
    ) {
      return;
    }

    setIsPublishing(true);
    setPublishError(null);
    // The conflicts below are answers of the publish request itself, not of
    // the client-side preparation (which reports its own conflicts).
    let publishRequested = false;

    try {
      if (pendingScheduleSyncPublishedPlanId) {
        publishRequested = true;
        const response = await publishCycleDraft(cycleId, {
          publishedPlanId: pendingScheduleSyncPublishedPlanId,
          ...(confirmedCancelRemovedRescheduledRef.current
            ? { cancelRemovedRescheduled: true }
            : {}),
        });
        setPendingScheduleSyncPublishedPlanId(null);
        confirmedCancelRemovedRescheduledRef.current = false;
        hydrateProgramDraft(response, { force: true });
        navigate(getCycleDetailsPath(cycleId), { replace: true });
        return;
      }

      const preparation = await prepareCycleDraftForPublish();
      if (preparation?.status !== "ready") {
        return;
      }

      confirmedCancelRemovedRescheduledRef.current = cancelRemovedRescheduled;
      publishRequested = true;
      const response = await publishCycleDraft(cycleId, {
        allowCrossDayDraft: preparation.metadata.allowCrossDayDraft,
        planId: preparation.metadata.cyclePlanId || activePlanId,
        ...(preparation.metadata.revision != null
          ? { expectedDraftRevision: preparation.metadata.revision }
          : {}),
        ...(cancelRemovedRescheduled ? { cancelRemovedRescheduled: true } : {}),
      });
      confirmedCancelRemovedRescheduledRef.current = false;
      hydrateProgramDraft(response, { force: true });
      navigate(getCycleDetailsPath(cycleId), { replace: true });
    } catch (publishError) {
      const partialPublishedPlanId =
        publishError?.code === "SCHEDULE_SYNC_FAILED"
          ? publishError?.details?.publishedPlanId || null
          : null;
      if (partialPublishedPlanId) {
        setPendingScheduleSyncPublishedPlanId(partialPublishedPlanId);
        setPublishError(
          buildUiError(
            publishError,
            "Cycle published, but scheduled sessions still need synchronization."
          )
        );
        return;
      }

      confirmedCancelRemovedRescheduledRef.current = false;

      if (publishRequested && publishError?.code === "OCCURRENCE_CONFLICT") {
        setRemovedRescheduledOccurrences(
          publishError?.details?.occurrences || []
        );
        return;
      }

      if (publishRequested && publishError?.code === "WORKOUT_OPEN_IN_TRAIN") {
        setPublishError({
          message:
            "This workout is open in Train. Finish or discard it, or undo your changes to it, then publish.",
          code: publishError.code,
        });
        return;
      }

      if (publishRequested && PUBLISH_RELOAD_CODES.has(publishError?.code)) {
        // Same recovery as an autosave conflict: the banner offers
        // "Reload latest version" and publishing stays blocked until then.
        setPendingScheduleSyncPublishedPlanId(null);
        updateDraftMetadata({
          saveState: "conflict",
          lastSaveErrorCode: publishError.code,
          lastSaveErrorMessage: publishError.message || null,
        });
        setPublishError({
          message:
            publishError.code === "NO_DRAFT_TO_PUBLISH"
              ? "This cycle was already published elsewhere. Reload the latest version."
              : "This cycle was updated elsewhere. Reload the latest version before publishing.",
          code: publishError.code,
        });
        return;
      }

      const didRecoverDraft = await handleDraftExpired(publishError, cycleId);
      if (didRecoverDraft) {
        return;
      }

      setPublishError(
        buildUiError(publishError, "Unable to publish cycle.")
      );
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublish = () => publishCycle();

  const handleConfirmCancelRemovedRescheduled = () => {
    setRemovedRescheduledOccurrences(null);
    publishCycle({ cancelRemovedRescheduled: true });
  };

  const handleToggleEditMode = () => {
    if (isRecoveringDraft) {
      return;
    }

    setIsEditMode((prev) => {
      if (prev) {
        setSelectedWorkoutOrderIndex(null);
      }

      return !prev;
    });
  };

  const handleWorkoutRowClick = (row) => {
    if (!row?.workout || isRecoveringDraft) {
      return;
    }

    if (isEditMode) {
      setSelectedWorkoutOrderIndex((prev) =>
        Number(prev) === Number(row.workout.orderIndex) ? null : row.workout.orderIndex
      );
      return;
    }

    navigate(
      getCycleWorkoutEditorPath(
        cycleId,
        selectedWeek?.weekNumber || programDraft.selectedWeek || 1,
        row.workout.orderIndex
      )
    );
  };

  const handleMoveSelectedWorkout = (direction) => {
    if (!selectedWorkoutRow?.workout || isRecoveringDraft) {
      return;
    }

    const canMove =
      direction === "previous" ? canMoveSelectedPrevious : canMoveSelectedNext;
    const targetRow =
      direction === "previous" ? previousSelectedRow : nextSelectedRow;

    if (!canMove || !targetRow?.day) {
      return;
    }

    moveSelectedWeekWorkoutToScheduledDay(
      selectedWorkoutRow.workout.orderIndex,
      targetRow.day
    );
  };

  const handleDuplicateSelectedWorkout = () => {
    if (!selectedWorkoutRow?.workout || !canDuplicateSelectedWorkout || isRecoveringDraft) {
      return;
    }

    duplicateSelectedWeekWorkout(
      selectedWorkoutRow.workout.orderIndex,
      isActiveEditMode ? firstAvailableDuplicateRow?.day || null : null
    );
  };

  const handleDeleteSelectedWorkout = () => {
    if (!canDeleteSelectedWorkout || isRecoveringDraft) {
      return;
    }

    setShowDeleteWorkoutConfirm(true);
  };

  const handleConfirmDeleteSelectedWorkout = () => {
    if (!canDeleteSelectedWorkout || !selectedWorkoutRow?.workout || isRecoveringDraft) {
      return;
    }

    deleteSelectedWeekWorkout(selectedWorkoutRow.workout.orderIndex);
    setShowDeleteWorkoutConfirm(false);
    setSelectedWorkoutOrderIndex(null);
  };

  if (isLoading) {
    return (
      <BuilderShell>
        <div className="lz-builder-loading" aria-label="Loading cycle builder">
          <Skeleton aria-label="Loading cycle builder" height="4rem" />
          <div className="lz-builder-loading__card">
            <Skeleton width="55%" height="1.5rem" />
            <Skeleton width="78%" shape="text" />
          </div>
          <Skeleton height="9rem" />
          <Skeleton height="13rem" />
        </div>
      </BuilderShell>
    );
  }

  if (loadError && !programDraft.weeks.length) {
    return (
      <BuilderShell>
        <div className="lz-builder-load-error">
          <EmptyState
            title="Unable to load cycle builder"
            body={loadError.message}
          />
          {loadError.code ? (
            <p className="lz-builder-alert__code">{loadError.code}</p>
          ) : null}
        </div>
      </BuilderShell>
    );
  }

  return (
    <BuilderShell>
      <header className="lz-builder-header">
        <button
          type="button"
          onClick={() => navigate(getCycleDetailsPath(cycleId))}
          className="lz-builder-header__button"
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className="lz-builder-header__identity">
          <h1 className="lz-builder-header__title">{programDraft.programName}</h1>
          <div className="lz-builder-header__meta">
            <span className="lz-builder-header__status">
              {draftMetadata.status} · {draftMetadata.temporalStatus}
            </span>
            <SaveStatusPill label={saveStatusLabel} />
          </div>
        </div>

        <Button
          variant="secondary"
          size="icon"
          onClick={openSettingsPanel}
          disabled={isRecoveringDraft}
          aria-label="Open cycle settings"
        >
          <GearIcon />
        </Button>
      </header>

      <main className="lz-builder-page">
        <div className="lz-builder-page__stack">
        {draftMetadata.recoveryMessage && (
          <div
            className={[
              "lz-builder-alert",
              isRecoveringDraft ? "lz-builder-alert--info" : "lz-builder-alert--danger",
            ].join(" ")}
          >
            <p className="lz-builder-alert__title">
              {isRecoveringDraft ? "Recovering draft" : "Draft recovery failed"}
            </p>
            <p className="lz-builder-alert__body">{draftMetadata.recoveryMessage}</p>
          </div>
        )}

        {draftMetadata.saveState === "conflict" && (
          <div className="lz-builder-alert lz-builder-alert--danger">
            <p className="lz-builder-alert__title">This cycle was updated elsewhere.</p>
            <p className="lz-builder-alert__body">
              Your unsaved changes are not being saved while this is unresolved.
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReloadAfterConflict}
              disabled={isReloadingAfterConflict}
              className="lz-builder-alert__action"
            >
              {isReloadingAfterConflict ? "Reloading..." : "Reload latest version"}
            </Button>
          </div>
        )}

        {autosaveError && (
          <div className="lz-builder-alert lz-builder-alert--danger">
            <p className="lz-builder-alert__title">Autosave failed</p>
            <p className="lz-builder-alert__body">{autosaveError.message}</p>
            {autosaveError.code && (
              <p className="lz-builder-alert__code">{autosaveError.code}</p>
            )}
          </div>
        )}

        <section className="lz-builder-summary">
          <div className="lz-builder-summary__heading">
            <div>
              <h2>Multi-Week Cycle</h2>
              <p>
                {formatDate(programDraft.startDate)} - {formatDate(programDraft.endDate)}
              </p>
            </div>
            <span className="lz-builder-summary__duration">
              {programDraft.programLength} weeks
            </span>
          </div>
        </section>

        <WeeklyVolumeChart
          weeks={weeklyVolumeData}
          onSelectWeek={setSelectedWeek}
        />

        <div className="lz-builder-week-rail">
          <WeekRail
            weeks={programDraft.weeks}
            selectedWeekNumber={programDraft.selectedWeek}
            onSelect={setSelectedWeek}
            distribute
          />
        </div>

        <div className="lz-product-metrics lz-product-metrics--section lz-product-metrics--aligned">
          {topStats.map((item) => (
            <MetricTile
              key={item.label}
              label={item.label}
              value={item.value}
              barValue={Number.parseFloat(item.width) || 0}
              barMax={100}
            />
          ))}
        </div>

        <MuscleDistributionPanel
          title="Weekly Muscle Distribution"
          volume={selectedWeekMuscleVolume}
          expanded={isMuscleDistributionExpanded}
          onExpandedChange={setIsMuscleDistributionExpanded}
        />

        <section className="lz-builder-schedule">
          <div className="lz-builder-schedule__header">
            <div>
              <h2 className="lz-builder-schedule__title">Workout Schedule</h2>
              {selectedWeekRangeLabel && (
                <p className="lz-builder-schedule__range">{selectedWeekRangeLabel}</p>
              )}
            </div>

            <button
              type="button"
              onClick={handleToggleEditMode}
              disabled={isRecoveringDraft}
              className="lz-builder-schedule__edit"
              aria-pressed={isEditMode}
            >
              {isEditMode ? "Done" : "Edit"}
            </button>
          </div>

          {/* Presentation-only reveal wrapper: the toolbar stays mounted so
              closing Edit Schedule can animate out as well as in. While closed
              it is collapsed to zero height, inert and aria-hidden, so it is
              neither focusable nor interactable. No toolbar logic, state or
              handler moved. */}
          <div
            className="lz-builder-collapse lz-builder-collapse--toolbar"
            data-expanded={isEditMode ? "true" : "false"}
            aria-hidden={isEditMode ? undefined : "true"}
            inert={isEditMode ? undefined : ""}
          >
            <div className="lz-builder-collapse__inner">
              <EditModeToolbar
                label={
                  selectedWorkoutOrderIndex == null ? "Select a workout" : "Edit week"
                }
                onMovePrevious={() => handleMoveSelectedWorkout("previous")}
                movePreviousDisabled={isRecoveringDraft || !canMoveSelectedPrevious}
                onMoveNext={() => handleMoveSelectedWorkout("next")}
                moveNextDisabled={isRecoveringDraft || !canMoveSelectedNext}
                onDuplicate={handleDuplicateSelectedWorkout}
                duplicateDisabled={isRecoveringDraft || !canDuplicateSelectedWorkout}
                onDelete={handleDeleteSelectedWorkout}
                deleteDisabled={isRecoveringDraft || !canDeleteSelectedWorkout}
              />
            </div>
          </div>

          <WeekdayScheduleGrid
            rows={weekdayRows}
            isEditMode={isEditMode}
            isActiveCycle={isActiveCycle}
            selectedWorkoutOrderIndex={selectedWorkoutOrderIndex}
            isRecoveringDraft={isRecoveringDraft}
            onWorkoutRowClick={handleWorkoutRowClick}
          />
        </section>
        </div>
      </main>

      <PublishBar>
        {publishError && (
          <div className="lz-builder-alert lz-builder-alert--danger">
            <p className="lz-builder-alert__title">
              {pendingScheduleSyncPublishedPlanId
                ? "Cycle published; schedule sync pending"
                : "Publish failed"}
            </p>
            <p className="lz-builder-alert__body">{publishError.message}</p>
            {publishError.code && (
              <p className="lz-builder-alert__code">{publishError.code}</p>
            )}
          </div>
        )}

        <PublishBarButton
          disabled={
            isPublishing ||
            isRecoveringDraft ||
            !hasPublishTarget ||
            (!pendingScheduleSyncPublishedPlanId &&
              draftMetadata.saveState === "conflict")
          }
          onClick={handlePublish}
        >
          <span>
            {isRecoveringDraft
              ? "Reloading latest draft..."
              : isPublishing
                ? "Publishing..."
                : pendingScheduleSyncPublishedPlanId
                  ? "Retry Schedule Sync"
                  : "Publish Cycle"}
          </span>
        </PublishBarButton>
      </PublishBar>

      <Sheet
        open={isSettingsOpen}
        onClose={closeSettingsPanel}
        title="Multi-Week Timeline"
        closeLabel="Close settings"
      >
            <div className="lz-builder-settings">
              <section className="lz-builder-settings__section">
                <div className="lz-builder-settings__copy">
                  <label
                    className="lz-builder-settings__label"
                    htmlFor="lz-builder-cycle-name"
                  >
                    Cycle Name
                  </label>
                </div>
                <input
                  id="lz-builder-cycle-name"
                  type="text"
                  className="lz-builder-settings__field"
                  value={settingsProgramName}
                  onChange={(event) =>
                    handleSettingsProgramNameChange(event.target.value)
                  }
                  placeholder="Cycle name"
                  aria-invalid={settingsNameError ? "true" : undefined}
                  aria-describedby={
                    settingsNameError ? "lz-builder-cycle-name-error" : undefined
                  }
                />
                {settingsNameError && (
                  <p
                    className="lz-builder-settings__error"
                    id="lz-builder-cycle-name-error"
                  >
                    {settingsNameError}
                  </p>
                )}
              </section>

              <section className="lz-builder-settings__section">
                <div className="lz-builder-settings__copy">
                  <label className="lz-builder-settings__label">
                    Start Date
                  </label>
                  <p className="lz-builder-settings__help">
                    {isUpcomingCycle
                      ? "Timeline edits are available for upcoming cycles only."
                      : isActiveCycle
                        ? "Start date is locked once the cycle is active."
                        : "Timeline edits are not available for this cycle."}
                  </p>
                </div>
                {!isSettingsWeekPickerOpen ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isStartDateEditable) {
                        return;
                      }

                      setSettingsWeekPickerMonth(
                        getStartOfMonthDate(settingsStartDate || minSettingsStartDate)
                      );
                      setIsSettingsWeekPickerOpen(true);
                    }}
                    disabled={!isStartDateEditable}
                    aria-expanded={false}
                    className="lz-builder-settings__field"
                  >
                    <span className="lz-builder-settings__field-value">
                      {selectedSettingsWeekRangeLabel}
                    </span>
                    <SettingsChevron direction="down" />
                  </button>
                ) : (
                  <div className="lz-builder-settings__picker">
                    <div className="lz-builder-settings__picker-header">
                      <button
                        type="button"
                        onClick={() =>
                          canGoToPreviousSettingsMonth &&
                          setSettingsWeekPickerMonth((currentMonth) =>
                            addMonths(currentMonth, -1)
                          )
                        }
                        disabled={!canGoToPreviousSettingsMonth}
                        className="lz-builder-settings__icon-button"
                        aria-label="Previous month"
                      >
                        <SettingsChevron direction="left" />
                      </button>

                      <p>
                        {settingsWeekPickerMonthLabel}
                      </p>

                      <button
                        type="button"
                        onClick={() =>
                          setSettingsWeekPickerMonth((currentMonth) =>
                            addMonths(currentMonth, 1)
                          )
                        }
                        className="lz-builder-settings__icon-button"
                        aria-label="Next month"
                      >
                        <SettingsChevron direction="right" />
                      </button>
                    </div>

                    <div className="lz-builder-settings__week-options">
                        {settingsVisibleWeekOptions.map((option) => {
                          const isSelected = option.value === settingsStartDate;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                handleSettingsStartDateChange(option.value);
                                setIsSettingsWeekPickerOpen(false);
                              }}
                              className="lz-builder-settings__week-option"
                              aria-pressed={isSelected}
                            >
                              <span className="lz-builder-settings__week-option-copy">
                                <span className="lz-builder-settings__week-option-label">
                                  {option.label}
                                </span>
                                {(option.isCurrentWeek || option.isSelectedOutsideMonth) && (
                                  <small>
                                    {option.isCurrentWeek
                                      ? "Earliest available"
                                      : "Selected outside this month"}
                                  </small>
                                )}
                              </span>
                              {isSelected && (
                                <span
                                  className="lz-builder-settings__week-option-check"
                                  aria-hidden="true"
                                >
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}
              </section>

              <section className="lz-builder-settings__section">
                <label className="lz-builder-settings__label">
                  End Date
                </label>
                <div className="lz-builder-settings__readonly">
                  {formatDate(settingsEndDate)}
                </div>
              </section>

              <section className="lz-builder-settings__section">
                <div className="lz-builder-settings__copy">
                  <label className="lz-builder-settings__label">
                    Duration In Weeks
                  </label>
                  <p className="lz-builder-settings__help">
                    {isUpcomingCycle
                      ? "Trim removes weeks from the end. Extend adds weeks at the end. Changes stay in draft until you publish."
                      : isActiveCycle
                        ? `Active cycles can be extended at the end only. Past weeks stay locked. Minimum: week ${currentActiveWeekNumber}.`
                        : "Duration changes are not available for this cycle."}
                  </p>
                </div>
                <div className="lz-builder-settings__duration">
                    <button
                      type="button"
                      onClick={decrementSettingsDuration}
                      disabled={!isDurationEditable || settingsDurationWeeks <= settingsMinDurationWeeks}
                      className="lz-builder-settings__icon-button"
                      aria-label="Decrease duration in weeks"
                    >
                      <span aria-hidden="true">−</span>
                    </button>

                    <div className="lz-builder-settings__duration-value">
                      <strong>
                        {settingsDurationWeeks}
                      </strong>
                      <span>Weeks</span>
                    </div>

                    <button
                      type="button"
                      onClick={incrementSettingsDuration}
                      disabled={!isDurationEditable || settingsDurationWeeks >= settingsMaxDurationWeeks}
                      className="lz-builder-settings__icon-button"
                      aria-label="Increase duration in weeks"
                    >
                      <span aria-hidden="true">+</span>
                    </button>
                </div>
              </section>

              {isSettingsExtension && (
                <section className="lz-builder-settings__section">
                  <div className="lz-builder-settings__copy">
                    <label className="lz-builder-settings__label">
                      Source Week For Added Weeks
                    </label>
                    <p className="lz-builder-settings__help">
                      Every added week will duplicate this one existing week.
                    </p>
                  </div>
                  <div
                    className="lz-builder-settings__source-weeks"
                    style={{
                      gridTemplateColumns: `repeat(${Math.max(
                        1,
                        settingsSourceWeekOptions.length
                      )}, minmax(0, 1fr))`,
                    }}
                  >
                    {settingsSourceWeekOptions.map((option) => {
                      const isSelected =
                        Number(option.value) === Number(settingsSourceWeekNumber);

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setSettingsSourceWeekNumber(option.value);
                            setSettingsError("");
                          }}
                          className="lz-builder-settings__source-week"
                          aria-pressed={isSelected}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {!canEditTimelineSettings && (
                <div className="lz-builder-alert">
                  Timeline edits are only available for upcoming or active cycles in V1.
                </div>
              )}

              {settingsError && (
                <div className="lz-builder-settings__error">
                  {settingsError}
                </div>
              )}

              <div className="lz-builder-settings__actions">
                <Button
                  variant="secondary"
                  onClick={closeSettingsPanel}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveSettings}
                  disabled={!canEditTimelineSettings || isSavingSettings || isRecoveringDraft}
                >
                  {isRecoveringDraft ? "Reloading..." : isSavingSettings ? "Saving..." : "Save"}
                </Button>
              </div>

              <Button
                variant="danger"
                onClick={() => !isRecoveringDraft && setShowDeleteConfirm(true)}
                disabled={isRecoveringDraft}
                className="lz-builder-settings__danger"
              >
                Delete Cycle
              </Button>
            </div>
      </Sheet>

      <Dialog
        open={Boolean(removedRescheduledOccurrences)}
        onClose={() => setRemovedRescheduledOccurrences(null)}
        title="Cancel moved sessions?"
        description="These moved sessions no longer exist in the new version. Cancel them and publish?"
        size="sm"
      >
        {removedRescheduledOccurrences?.length > 0 && (
          <ul className="lz-builder-dialog-list">
            {removedRescheduledOccurrences.map((occurrence) => (
              <li key={occurrence.occurrenceKey || formatRemovedOccurrence(occurrence)}>
                {formatRemovedOccurrence(occurrence)}
              </li>
            ))}
          </ul>
        )}
        <div className="lz-builder-dialog-actions">
          <Button variant="secondary" onClick={() => setRemovedRescheduledOccurrences(null)}>
            Keep editing
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmCancelRemovedRescheduled}
            disabled={isPublishing || isRecoveringDraft}
          >
            Cancel sessions and publish
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={showGraceWindowConfirm}
        onClose={() => setShowGraceWindowConfirm(false)}
        title="Continue editing?"
        description="A new local day just started. Continue editing temporarily until 00:20?"
        size="sm"
      >
        <div className="lz-builder-dialog-actions">
          <Button variant="secondary" onClick={() => setShowGraceWindowConfirm(false)}>
            Cancel
          </Button>
          <Button onClick={handleAcceptGraceWindow}>Continue editing</Button>
        </div>
      </Dialog>

      <Dialog
        open={showConflictReloadConfirm}
        onClose={() => setShowConflictReloadConfirm(false)}
        title="Reload latest version?"
        description="Your unsaved local changes will be discarded."
        size="sm"
      >
        <div className="lz-builder-dialog-actions">
          <Button variant="secondary" onClick={() => setShowConflictReloadConfirm(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmReloadAfterConflict}>Reload latest version</Button>
        </div>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Confirm deletion of this cycle?"
        description="Delete this multi-week cycle and all of its versions. This action can't be undone."
        size="sm"
      >
        <div className="lz-builder-dialog-actions">
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteCycle}
            disabled={isDeletingCycle || isRecoveringDraft}
          >
            {isRecoveringDraft
              ? "Reloading..."
              : isDeletingCycle
                ? "Deleting..."
                : "Delete Cycle"}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(showDeleteWorkoutConfirm && selectedWorkoutRow?.workout)}
        onClose={() => setShowDeleteWorkoutConfirm(false)}
        title="Delete this workout?"
        description={
          selectedWorkoutRow?.workout?.name
            ? `Remove "${selectedWorkoutRow.workout.name}" from this week.`
            : "Remove this workout from the current week."
        }
        size="sm"
      >
        <div className="lz-builder-dialog-actions">
          <Button variant="secondary" onClick={() => setShowDeleteWorkoutConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDeleteSelectedWorkout}
            disabled={isRecoveringDraft}
          >
            Delete workout
          </Button>
        </div>
      </Dialog>
    </BuilderShell>
  );
}
